"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./LazyMedia.module.scss"; // 👈 import spinner styles
import { he, tr } from "date-fns/locale";
import Shimmer from "../Shimmer";
import ShimmerItem from "../Shimmer/ShimmerItem";

interface LazyMediaProps {
  src: string;
  tag?: "iframe" | "embed";
  title?: string;
  type?: string;
  height?: string | number;
  width?: string | number;
  className?: string;
  rounded?: boolean;
  embedClassName?: string;
  allowFullScreen?: boolean;
  allow?: string;
  iframeClassName?: string;
  onLoad?: () => void;
  loader?: "shimmer" | "spinner";
}

export default function LazyMedia({
  src,
  tag = "iframe",
  title = "Embedded Content",
  type = "application/pdf",
  height,
  width,
  className = "",
  rounded = true,
  embedClassName = "",
  allowFullScreen = true,
  allow = "",
  iframeClassName = "",
  onLoad = () => {},
  loader = "spinner",
}: LazyMediaProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1, rootMargin: "30px" }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const containerStyle: React.CSSProperties = {};
  if (height) {
    containerStyle.height = height;
    containerStyle.minHeight = height;
  }
  if (width) {
    containerStyle.width = width;
  }

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className={`w-100 h-100 ${className}`}
    >
      {isVisible ? (
        tag === "iframe" ? (
          <iframe
            src={src}
            title={title}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: height || "100%" }}
            loading="lazy"
            allowFullScreen={allowFullScreen}
            className={`${rounded ? "rounded" : ""} ${iframeClassName}`}
            allow={allow}
            onLoad={onLoad}
          />
        ) : (
          <embed
            src={src}
            type={type}
            width="100%"
            height="100%"
            className={`${rounded ? "rounded" : ""} ${embedClassName}`}
          />
        )
      ) : (
        <div
          className={`d-flex justify-content-center align-items-center h-100 ${
            rounded ? "rounded" : ""
          }`}
          style={{ backgroundColor: "#f8f9fa" }}
        >
          {loader === "spinner" && <div className={styles.spinner} />}{" "}
          {/* 👈 your custom spinner */}
          {loader === "shimmer" && (
            <Shimmer>
              <ShimmerItem height="24px" width="100%" />
              <ShimmerItem height="16px" width="50%" align="center" />
              <ShimmerItem height="16px" width="80%" align="center" />
              <ShimmerItem className="mt-4" height="16px" width="100%" />
              <ShimmerItem height="20px" width="100%" />
              <ShimmerItem height="20px" width="100%" />
              <ShimmerItem height="20px" width="100%" />
            </Shimmer>
          )}
        </div>
      )}
    </div>
  );
}
